import {
  FieldsEnum,
  FormsEnum,
  MessagesEnum,
  PaymentTypeEnum,
  SectionsEnum,
} from 'app/shared/enums';
import {
  clearFieldValue,
  getDictionaryFromSelector,
  getFormValue,
  getVisibleDictionaryFromSelector,
  setHiddenDictionaries,
  setMessageVisibility,
  setSectionMessageVisibility,
  changeFieldValueAndUpdate,
  setQuestionVisibility,
  getFormConfig,
  getOfferFromSelector,
  getPromotion,
  setDictionaryEntryVisibility,
  getQuestion,
  getIsOperatorFromSelector,
} from 'app/services/businessFunctionHelpers';
import { DictionaryEntry } from 'app/services/dict.service';
import { Message } from 'app/shared/interfaces/Message.interface';
import { ScrollService } from 'app/services/scroll.service';
import { isFundraisingPromotion } from 'app/shared/helpers/isFundraisingPromotion';

const formName = FormsEnum.CheckoutPaymentForm;

const isInternalTransfer = ({ key }: DictionaryEntry) =>
  key === PaymentTypeEnum.InternalTransfer;

const paymentTypeChanged = () => {
  const paymentFormConfig = getFormConfig(formName);
  const paymentType = getFormValue(FieldsEnum.PaymentType, formName);
  const { code, promotionAmount } = getPromotion();
  const messageVisible = Boolean(
    paymentType !== PaymentTypeEnum.InternalTransfer && code && promotionAmount
  );
  const messageVisibleInternalTransfer = Boolean(
    paymentType === PaymentTypeEnum.InternalTransfer && code && promotionAmount
  );
  const isQuestionVisible = Boolean(
    getQuestion(FieldsEnum.CheckoutPaymentType, formName)?.visible
  );

  const paymentPromotionCodeWrongCodeMessage = paymentFormConfig?.sections?.[
    SectionsEnum.PaymentSectionPromotionCode
  ]?.messages?.find(
    (msg: Message) => msg.key === MessagesEnum.PaymentPromotionCodeWrongCode
  );

  if (isQuestionVisible) {
    ScrollService.scrollToQuestion(FieldsEnum.PaymentType);
  }

  if (
    paymentPromotionCodeWrongCodeMessage &&
    paymentPromotionCodeWrongCodeMessage.visible
  ) {
    return;
  }

  if (getPromotion().errorKey) {
    return;
  }

  if (isFundraisingPromotion(getPromotion().promotionType)) {
    setSectionMessageVisibility({
      formName,
      key: MessagesEnum.PaymentPromotionCodeMessage,
      visible: false,
    });
    setSectionMessageVisibility({
      formName,
      key: MessagesEnum.PaymentPromotionCodeMessageInternalTransfer,
      visible: false,
    });
    setSectionMessageVisibility({
      formName,
      key: MessagesEnum.PaymentPromotionVoucher,
      visible: false,
    });
    return;
  }

  setSectionMessageVisibility({
    formName,
    key: MessagesEnum.PaymentPromotionCodeMessage,
    visible: messageVisible,
  });

  setSectionMessageVisibility({
    formName,
    key: MessagesEnum.PaymentPromotionCodeMessageInternalTransfer,
    visible: messageVisibleInternalTransfer,
  });
};

export const tryCheckAndHidePaymentMethod = () => {
  const numberOfInstallments = getFormValue(
    FieldsEnum.NumberOfInstallments,
    formName
  );

  const dictionary = getVisibleDictionaryFromSelector(
    'payment.paymentType_dictionary',
    formName
  );

  setQuestionVisibility({
    key: 'payment.paymentType',
    visible: !!(dictionary && dictionary.length > 1) && !!numberOfInstallments,
    formName,
  });

  const hasOneEntry = dictionary?.length === 1;

  if (hasOneEntry) {
    changeFieldValueAndUpdate(
      'payment.paymentType',
      dictionary![0].key,
      formName
    );
    paymentTypeChanged();
  }
};

export const hideHiProTransferByDefault = () => {
  if (!isHiProPaymentTypeAvailable()) {
    return;
  }

  setDictionaryEntryVisibility({
    dictionaryKey: 'payment.paymentType',
    entryKey: PaymentTypeEnum.HiProTransfer,
    visible: false,
    formName,
  });
};

const isHiProPaymentTypeAvailable = () => {
  const dictionary = getDictionaryFromSelector(
    formName,
    'payment.paymentType_dictionary'
  );

  const hasHiProTransfer = !!dictionary?.find(
    (entry: DictionaryEntry) => entry.key === PaymentTypeEnum.HiProTransfer
  );

  return hasHiProTransfer;
};

const payment = {
  paymentTypeChanged,
  numberOfInstallmentsChanged() {
    const numberOfInstallments = getFormValue(
      FieldsEnum.NumberOfInstallments,
      formName
    );
    const paymentTypesDictionary =
      getDictionaryFromSelector(formName, 'payment.paymentType_dictionary') ||
      [];

    const numberOfInstallmentsDictionary =
      getDictionaryFromSelector(
        formName,
        'payment.numberOfInstallments_dictionary'
      ) || [];
    const offer = getOfferFromSelector();
    const isOperator = getIsOperatorFromSelector();

    const hasInstallmentsAvailable = numberOfInstallmentsDictionary.some(
      (entry) => entry.key > 1
    );

    const hasInstallmentsSelected = numberOfInstallments > 1;

    const hasHiProInstallmentsSelected = numberOfInstallments === 'hipro';

    const hasAnyInstallmentOptionButNotSelected =
      (hasInstallmentsAvailable || isHiProPaymentTypeAvailable()) &&
      !hasInstallmentsSelected &&
      !hasHiProInstallmentsSelected;

    const shouldScrollAfterChange = hasAnyInstallmentOptionButNotSelected;

    let hiddenPaymentTypesDictionaryEntries = [];

    if (isHiProPaymentTypeAvailable()) {
      if (hasHiProInstallmentsSelected) {
        hiddenPaymentTypesDictionaryEntries.push(
          'payment.paymentType_internal_transfer',
          'payment.paymentType_traditional_transfer'
        );
      } else {
        hiddenPaymentTypesDictionaryEntries.push(
          'payment.paymentType_hi_pro_transfer'
        );
      }
    }

    setHiddenDictionaries({
      formName,
      hiddenDictionaryEntries: hiddenPaymentTypesDictionaryEntries,
    });

    if (paymentTypesDictionary?.length > 1 || isHiProPaymentTypeAvailable()) {
      clearFieldValue(FieldsEnum.PaymentType, formName);
      tryCheckAndHidePaymentMethod();
    }

    if (shouldScrollAfterChange) {
      ScrollService.scrollToQuestion(FieldsEnum.NumberOfInstallments);
    }

    if (isOperator || !paymentTypesDictionary.some(isInternalTransfer)) {
      return;
    }

    setMessageVisibility({
      formName,
      key: 'internalTransferOnlyIfOneInstallmentSelected',
      visible:
        hasInstallmentsSelected &&
        !offer?.shouldAllowInstallmentForInternalTransfer,
    });
  },

  paymentMethodChanged() {
    const paymentMethod = getFormValue(
      FieldsEnum.CheckoutPaymentMethod,
      formName
    );

    if (!paymentMethod) {
      return;
    }

    ScrollService.scrollToQuestion(FieldsEnum.ButtonConsentsPayment);
  },

  tryCheckAndHidePaymentMethod,
  hideHiProTransferByDefault,
};

export default payment;
